import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usersAPI } from '../services/api';
import { LoginBack, LoginHeader } from '../components/Common/Card';
import { AuthDesktopHeader } from '../components/Common/Dialog';
import AddressInput from '../components/Common/AddressInput';
import { Button } from '../components/Common/Button';
import { Check } from '../components/Icons/CustomIcons';

/**
 * KontoVervollstaendigen
 *
 * Wird nach einem Social-Login (z.B. Google) für frisch angelegte Konten
 * angezeigt. Vorname/Name kommen vom Provider, die Adresse kann er aber nicht
 * liefern – daher wird hier einmalig der optionale Adress-Schritt gezeigt
 * (entspricht Schritt 3 der Registrierung). Überspringbar.
 */
const KontoVervollstaendigen = () => {
  const { isAuthenticated, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const returnTo = params.get('von') || '/';

  const [address, setAddress] = useState({ strasse: '', plz: '', wohnort: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const addressValidateRef = useRef(null);

  // Desktop ab 1024px – identische Logik wie MobileLoginPage
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Nicht eingeloggt → zur Anmeldung
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/anmelden', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  const bgStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/${
      isDesktop ? 'GrosselternMitKindern.png' : 'GrosselternMitKindernLeft.png'
    })`,
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');

    // Ohne Eingabe: nichts zu speichern, direkt weiter
    if (!address.strasse.trim() && !address.plz.trim() && !address.wohnort.trim()) {
      navigate(returnTo, { replace: true });
      return;
    }

    if (address.strasse.trim()) {
      const addrErr = await addressValidateRef.current?.();
      if (addrErr) { setError(addrErr); return; }
    }

    setSaving(true);
    try {
      const response = await usersAPI.updateSettings({
        strasse: address.strasse,
        plz: address.plz,
        wohnort: address.wohnort,
      });
      setUser(response.data);
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen. Bitte versuchen Sie es erneut.');
      setSaving(false);
    }
  };

  const handleSkip = () => navigate(returnTo, { replace: true });

  return (
    <div className="auth-fullpage" style={bgStyle}>
      <LoginBack returnTo={returnTo} />
      <LoginHeader
        title="Fast geschafft!"
        subtitle="Hinterlegen Sie Ihre Adresse als Startpunkt für die Aktivitätensuche – oder überspringen Sie diesen Schritt."
      />
      <div className="auth-fullpage-panel">
        <AuthDesktopHeader
          title="Fast geschafft!"
          subtitle="Hinterlegen Sie Ihre Adresse als Startpunkt für die Aktivitätensuche – oder überspringen Sie diesen Schritt."
        />
        <div className="auth-panel">
          <div className="auth-panel-body">
            <div className="mobile-login-content">
              <div className="register-tab">
                <h2 className="headings Font-H2-S-120-T">Ihre Adresse</h2>
                <form onSubmit={handleSave} className="ml-form register-form">
                  {error && <div className="error-message" role="alert">{error}</div>}
                  <p className="step-info">Adresse (Optional)</p>
                  <AddressInput
                    street={address.strasse}
                    plz={address.plz}
                    city={address.wohnort}
                    onChange={({ street, plz, city }) =>
                      setAddress({ strasse: street, plz, wohnort: city })
                    }
                    validateRef={addressValidateRef}
                    className="address-input--register"
                  />
                  <div className="flex gap-3 mt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleSkip}
                      disabled={saving}
                      className="flex-1" 
                    >
                      Überspringen
                    </Button>
                    <Button type="submit" className="flex-1" disabled={saving}>
                      <Check size={20} aria-hidden="true" /> Speichern
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KontoVervollstaendigen;
